import { Injectable } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import { LoginDetailsService } from 'src/app/core/login/services/login-details.service';

@Injectable({
  providedIn: 'root'
})
export class DateTimeService {
  private defaultFormat = 'yyyy-MM-dd HH:mm:ss';
  private defaultLocale = 'en-US';

  months = ['Jan','Feb','Mar','Apr','May','June','July','Aug','Sept','Oct','Nov','Dec'];
  
  constructor(private datePipe: DatePipe, private loginDetailsService: LoginDetailsService) { }

  // returns Date format using supportedOfferDateTimeFormat (e.g. 'E, MMM d' -> 'Mon, Apr 20')
  formatShortDate(date: string, hideOfferTime: boolean = false) {
    const dateFormat = this.loginDetailsService.supportedOfferDateTimeFormat || 'E, MMM d';
    const timePattern = /\s*,?\s*\d{1,2}:\d{2}(\s*[AP]M)?/i;
    
    if (!date) return '';
    
    try {
      const formattedDate = this.datePipe.transform(date, dateFormat) || '';
      
      if (hideOfferTime) {
        return formattedDate.replace(timePattern, '').trim();
      } else {
        // If time should be shown, append time in 'h:mm a' format only if not already included
        const timeAlreadyIncluded = timePattern.test(formattedDate);
        if (timeAlreadyIncluded) {
          return formattedDate;
        }
        const timeString = this.datePipe.transform(date, 'h:mm a') || '';
        return formattedDate + ' ' + timeString;
      }
    } catch (e) {
      console.error('Date formatting error:', e);
      return '';
    }
  }

  formatShortTime(date: string) {
    let formattedTime = ' AM';
    let hours = new Date(date).getHours();

    if( hours > 12 ) {
      hours -= 12;
      formattedTime = hours + ' PM';
    }
    else {
     formattedTime = hours + formattedTime;
    }
    return formattedTime;
  }

  formatCustomDate(date: Date | string | number, formatStr?: string, locale?: string): string {
    if (!date) return '';
    try {
      return formatDate(date, formatStr || this.defaultFormat, locale || this.defaultLocale);
    } catch (e) {
      console.error('Date formatting error:', e);
      return '';
    }
  }

  formatRSVPDate(dateString: string) {
    const dateFormat = this.loginDetailsService.supportedOfferDateTimeFormat || 'E, MMM d';
    
    if (!dateString) return '';
    
    try {
      // Check if the string contains "Valid" prefix and date range
      // Examples: "Valid 04/01/26-04/30/26", "Valid Thursday or Friday, 04/30/26-05/01/26"
      const validPrefixMatch = dateString.match(/^(Valid\s+(?:\w+\s+(?:or\s+)?\w+,\s+)?)(.+)$/i);
      
      if (validPrefixMatch) {
        const datesPart = validPrefixMatch[2].trim();
        
        // Split by hyphen to get date range (handle both "04/01/26-04/30/26" and "04/27/2026 - 05/03/2026")
        const dates = datesPart.split(/\s*-\s*/);
        
        if (dates.length === 2) {
          // Format both start and end dates
          const startDateFormatted = this.datePipe.transform(dates[0].trim(), dateFormat);
          const endDateFormatted = this.datePipe.transform(dates[1].trim(), dateFormat);
          
          if (startDateFormatted && endDateFormatted) {
            return `${startDateFormatted} - ${endDateFormatted}`;
          }
        }
      }
      
      // If no "Valid" prefix or not a range, try formatting as a single date
      return this.datePipe.transform(dateString, dateFormat) || dateString;
    } catch (e) {
      console.error('RSVP Date formatting error:', e);
      // Return original string if formatting fails
      return dateString;
    }
  }

  // Checks if two dates are on the same day AND within 24 hours
  // return TRUE if no endDate provided
  isOneDayEvent(startDate: string, endDate: string): boolean {
    if (!endDate) return true;
    if (!startDate) return false;
    
    const d1 = new Date(startDate);
    const d2 = new Date(endDate);
    
    // Check if same calendar day
    const isSameCalendarDay = d1.getFullYear() === d2.getFullYear() &&
                               d1.getMonth() === d2.getMonth() &&
                               d1.getDate() === d2.getDate();
    
    // Check if within 24 hours (86400000 milliseconds)
    const timeDifference = Math.abs(d2.getTime() - d1.getTime());
    const isWithin24Hours = timeDifference <= 86400000;
    
    return isSameCalendarDay && isWithin24Hours;
  }
}